// API client. Pulls CSRF token from cookie and attaches to all state-changing requests.
// Throws ApiError on non-2xx — components catch and surface to user.

const API_BASE = import.meta.env.VITE_API_URL || '';
const CSRF_COOKIE = '__Host-skb_csrf';

export class ApiError extends Error {
  constructor(status, code, message, details) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function getCookie(name) {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

async function request(path, { method = 'GET', body, headers = {}, signal } = {}) {
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const init = { method, credentials: 'include', headers: { ...headers }, signal };

  if (body !== undefined) {
    init.headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  if (method !== 'GET' && method !== 'HEAD') {
    const csrf = getCookie(CSRF_COOKIE);
    if (csrf) init.headers['X-CSRF-Token'] = csrf;
  }

  const res = await fetch(url, init);
  let data = null;
  try { data = await res.json(); } catch { /* no body */ }

  if (!res.ok || (data && data.ok === false)) {
    const err = data?.error || { code: 'http', message: `HTTP ${res.status}` };
    throw new ApiError(res.status, err.code, err.message, err.details);
  }
  return data?.data ?? data;
}

export const api = {
  // Auth
  signup: (body) => request('/api/v1/auth/signup', { method: 'POST', body }),
  login: (body) => request('/api/v1/auth/login', { method: 'POST', body }),
  logout: () => request('/api/v1/auth/logout', { method: 'POST' }),
  me: () => request('/api/v1/auth/me'),
  magicLinkRequest: (body) => request('/api/v1/auth/magic-link/request', { method: 'POST', body }),
  magicLinkVerify: (body) => request('/api/v1/auth/magic-link/verify', { method: 'POST', body }),

  // Company
  getMyCompany: () => request('/api/v1/companies/me'),
  updateMyCompany: (body) => request('/api/v1/companies/me', { method: 'PATCH', body }),
  getBilling: () => request('/api/v1/companies/me/billing'),

  // Users
  listUsers: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/api/v1/users${qs ? '?' + qs : ''}`);
  },
  inviteUser: (body) => request('/api/v1/users', { method: 'POST', body }),
  bulkInviteUsers: (body) => request('/api/v1/users/bulk', { method: 'POST', body }),
  getUser: (id) => request(`/api/v1/users/${id}`),
  updateUser: (id, body) => request(`/api/v1/users/${id}`, { method: 'PATCH', body }),
  deactivateUser: (id) => request(`/api/v1/users/${id}`, { method: 'DELETE' }),

  // Groups
  listGroups: () => request('/api/v1/groups'),
  createGroup: (body) => request('/api/v1/groups', { method: 'POST', body }),
  getGroup: (id) => request(`/api/v1/groups/${id}`),
  updateGroup: (id, body) => request(`/api/v1/groups/${id}`, { method: 'PATCH', body }),
  deleteGroup: (id) => request(`/api/v1/groups/${id}`, { method: 'DELETE' }),
  addGroupMember: (id, body) => request(`/api/v1/groups/${id}/members`, { method: 'POST', body }),

  // Modules + assessments
  listModules: () => request('/api/v1/modules'),
  getModule: (id) => request(`/api/v1/modules/${id}`),

  listMyAssessments: () => request('/api/v1/assessments'),
  startAssessment: (body) => request('/api/v1/assessments/start', { method: 'POST', body }),
  assignAssessments: (body) => request('/api/v1/assessments/assign', { method: 'POST', body }),
  getSession: (id) => request(`/api/v1/assessments/${id}`),
  submitResponse: (id, body) => request(`/api/v1/assessments/${id}/respond`, { method: 'POST', body }),
  completeSession: (id) => request(`/api/v1/assessments/${id}/complete`, { method: 'POST' }),
  getResults: (id) => request(`/api/v1/assessments/${id}/results`),

  // Insights
  individualInsights: (userId) => request(`/api/v1/insights/individual/${userId}`),
  teamInsights: (groupId) => request(`/api/v1/insights/team/${groupId}`),
  companyInsights: () => request('/api/v1/insights/company'),
  trendInsights: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/api/v1/insights/trends${qs ? '?' + qs : ''}`);
  },

  // Billing
  createCheckoutSession: () => request('/api/v1/billing/checkout-session', { method: 'POST' }),
  createPortalSession: () => request('/api/v1/billing/portal-session', { method: 'POST' }),

  // Audit
  listAuditLog: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/api/v1/audit-log${qs ? '?' + qs : ''}`);
  }
};
